import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import './LessonResults.css';

const TEXT = {
    en: {
        title: 'Last results',
        loading: 'Loading results...',
        none: 'No results yet. Complete this lesson first.',
        back: 'Go back',
        completed: 'Completed {date}',
        correctLabel: 'questions correct',
        byQuestion: 'Question by question',
        question: 'Question {n}',
        tagCorrect: 'Correct',
        tagYours: 'Your answer',
        redo: 'Redo lesson →',
    },
    it: {
        title: 'Ultimi risultati',
        loading: 'Caricamento dei risultati...',
        none: 'Ancora nessun risultato. Completa prima questa lezione.',
        back: 'Indietro',
        completed: 'Completata il {date}',
        correctLabel: 'domande corrette',
        byQuestion: 'Domanda per domanda',
        question: 'Domanda {n}',
        tagCorrect: 'Corretta',
        tagYours: 'La tua risposta',
        redo: 'Rifai la lezione →',
    },
    es: {
        title: 'Últimos resultados',
        loading: 'Cargando resultados...',
        none: 'Todavía no hay resultados. Completa primero esta lección.',
        back: 'Volver',
        completed: 'Completada el {date}',
        correctLabel: 'preguntas correctas',
        byQuestion: 'Pregunta por pregunta',
        question: 'Pregunta {n}',
        tagCorrect: 'Correcta',
        tagYours: 'Tu respuesta',
        redo: 'Repetir la lección →',
    },
    fr: {
        title: 'Derniers résultats',
        loading: 'Chargement des résultats...',
        none: 'Pas encore de résultats. Termine d’abord cette leçon.',
        back: 'Retour',
        completed: 'Terminée le {date}',
        correctLabel: 'questions réussies',
        byQuestion: 'Question par question',
        question: 'Question {n}',
        tagCorrect: 'Correcte',
        tagYours: 'Ta réponse',
        redo: 'Refaire la leçon →',
    },
    pt: {
        title: 'Últimos resultados',
        loading: 'Carregando resultados...',
        none: 'Ainda não há resultados. Conclua primeiro esta lição.',
        back: 'Voltar',
        completed: 'Concluída em {date}',
        correctLabel: 'perguntas corretas',
        byQuestion: 'Pergunta por pergunta',
        question: 'Pergunta {n}',
        tagCorrect: 'Correta',
        tagYours: 'Sua resposta',
        redo: 'Refazer a lição →',
    },
};

const DATE_LOCALES = { en: 'en-GB', it: 'it-IT', es: 'es-ES', fr: 'fr-FR', pt: 'pt-BR' };

function LessonResults() {
    const navigate = useNavigate();
    const { topicSlug, lessonSlug } = useParams();
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const { interfaceLanguage } = useLanguage();
    const { activeLanguage } = useUserLanguage();
    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';
    const s = (key, values) => {
        let text = TEXT[uiLang][key] || TEXT.en[key];
        Object.keys(values || {}).forEach(name => {
            text = text.replace(`{${name}}`, values[name]);
        });
        return text;
    };
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();

    useEffect(() => {
        const load = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) { navigate('/signin'); return; }
            const { data } = await supabase
                .from('lesson_results')
                .select('*')
                .eq('user_id', user.id)
                .eq('topic_slug', topicSlug)
                .eq('lesson_slug', lessonSlug)
                .eq('target_language', targetLang)
                .maybeSingle();
            setResults(data);
            setLoading(false);
        };
        load();
    }, [topicSlug, lessonSlug, navigate, targetLang]);

    const formatDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleDateString(DATE_LOCALES[uiLang] || 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    return (
        <div className="lr-screen">
            <div className="lr-top-bar">
                <button className="lr-back" onClick={() => navigate(-1)}>←</button>
                <div className="lr-title">{s('title')}</div>
                <div style={{ width: 32 }}></div>
            </div>

            {loading && (
                <div className="lr-state">
                    <div className="lr-state-text">{s('loading')}</div>
                </div>
            )}

            {!loading && !results && (
                <div className="lr-state">
                    <div className="lr-state-icon">📋</div>
                    <div className="lr-state-text">{s('none')}</div>
                    <button className="lr-action-btn" onClick={() => navigate(-1)}>{s('back')}</button>
                </div>
            )}

            {!loading && results && (
                <>
                    <div className="lr-summary">
                        <div className="lr-summary-date">{s('completed', { date: formatDate(results.completed_at) })}</div>
                        <div className="lr-summary-score">
                            <span className="lr-score-num">{results.correct_count}</span>
                            <span className="lr-score-sep">/</span>
                            <span className="lr-score-total">{results.total_questions}</span>
                        </div>
                        <div className="lr-score-label">{s('correctLabel')}</div>
                        <div className="lr-score-bar-wrap">
                            <div
                                className="lr-score-bar-fill"
                                style={{ width: `${results.total_questions > 0 ? (results.correct_count / results.total_questions) * 100 : 0}%` }}
                            ></div>
                        </div>
                    </div>

                    <div className="lr-questions-label">{s('byQuestion')}</div>

                    <div className="lr-questions">
                        {(results.questions || []).map((q, i) => (
                            <div key={i} className={`lr-question-card ${q.isCorrect ? 'correct' : 'wrong'}`}>
                                <div className="lr-question-header">
                                    <div className="lr-question-icon">{q.isCorrect ? '✓' : '✕'}</div>
                                    <div className="lr-question-num">{s('question', { n: i + 1 })}</div>
                                </div>
                                <div className="lr-question-text">{q.question}</div>
                                <div className="lr-options">
                                    {q.options.map((option, j) => (
                                        <div
                                            key={j}
                                            className={`lr-option ${j === q.correct ? 'correct' : ''} ${j === q.chosen && !q.isCorrect ? 'wrong' : ''}`}
                                        >
                                            <span className="lr-option-letter">{['A', 'B', 'C', 'D'][j]}</span>
                                            <span className="lr-option-text">{option}</span>
                                            {j === q.correct && <span className="lr-option-tag correct">{s('tagCorrect')}</span>}
                                            {j === q.chosen && !q.isCorrect && <span className="lr-option-tag wrong">{s('tagYours')}</span>}
                                        </div>
                                    ))}
                                </div>
                                <div className="lr-explanation">{q.explanation}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ height: 32 }}></div>
                    <div className="lr-footer">
                        <button className="lr-redo-btn" onClick={() => navigate(`/lesson/${topicSlug}__${lessonSlug}`)}>
                            {s('redo')}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default LessonResults;